/**
 * Shared low-level IndexedDB connection for the terminal's offline storage.
 *
 * A single database (`seven-punches`) holds every offline-related object
 * store. Each store gets its own thin, typed module built on top of the
 * helpers here:
 *
 *   - `offlineStorage.ts`    — `offline_state`   (offlineSince timer, API-4377)
 *   - `offlinePunchQueue.ts` — `offline_punches` (punch queue, API-4378)
 *   - `ldrCache.ts`          — `ldr_cache`        (role/department cache, API-4378)
 *   - `roleMappingCache.ts`  — `role_mapping_cache` (external role ID cache, API-4674)
 *
 * This module owns the single `onupgradeneeded` handler (IndexedDB requires
 * schema changes to happen in one place per database) and exposes generic
 * key/value + index helpers so the per-store modules stay declarative.
 */

export const DB_NAME = 'seven-punches';
export const DB_VERSION = 3;

export const OFFLINE_STATE_STORE = 'offline_state';
export const OFFLINE_PUNCHES_STORE = 'offline_punches';
export const LDR_CACHE_STORE = 'ldr_cache';
export const ROLE_MAPPING_CACHE_STORE = 'role_mapping_cache';

// Index on `offline_punches` used to scope reads/deletes to a single company.
export const COMPANY_ID_INDEX = 'company_id';

let dbPromise: Promise<IDBDatabase> | null = null;

export function openDatabase(): Promise<IDBDatabase> {
    if (!dbPromise) {
        dbPromise = new Promise((resolve, reject) => {
            if (typeof indexedDB === 'undefined') {
                reject(new Error('IndexedDB is not available in this environment'));
                return;
            }

            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onupgradeneeded = () => {
                const db = request.result;

                if (!db.objectStoreNames.contains(OFFLINE_STATE_STORE)) {
                    db.createObjectStore(OFFLINE_STATE_STORE);
                }

                if (!db.objectStoreNames.contains(OFFLINE_PUNCHES_STORE)) {
                    const punches = db.createObjectStore(OFFLINE_PUNCHES_STORE, {
                        keyPath: 'id',
                        autoIncrement: true
                    });
                    punches.createIndex(COMPANY_ID_INDEX, 'company_id', {
                        unique: false
                    });
                }

                if (!db.objectStoreNames.contains(LDR_CACHE_STORE)) {
                    db.createObjectStore(LDR_CACHE_STORE);
                }

                if (!db.objectStoreNames.contains(ROLE_MAPPING_CACHE_STORE)) {
                    db.createObjectStore(ROLE_MAPPING_CACHE_STORE);
                }
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () =>
                reject(request.error ?? new Error('Failed to open IndexedDB'));
        });
    }
    return dbPromise;
}

export function getValue<T>(store: string, key: string): Promise<T | undefined> {
    return openDatabase().then(
        db =>
            new Promise<T | undefined>((resolve, reject) => {
                const tx = db.transaction(store, 'readonly');
                const req = tx.objectStore(store).get(key);
                req.onsuccess = () => resolve(req.result as T | undefined);
                req.onerror = () =>
                    reject(
                        req.error ?? new Error(`Failed to read ${key} from ${store}`)
                    );
            })
    );
}

export function setValue(store: string, key: string, value: unknown): Promise<void> {
    return openDatabase().then(
        db =>
            new Promise<void>((resolve, reject) => {
                const tx = db.transaction(store, 'readwrite');
                tx.objectStore(store).put(value, key);
                tx.oncomplete = () => resolve();
                tx.onerror = () =>
                    reject(
                        tx.error ?? new Error(`Failed to write ${key} to ${store}`)
                    );
            })
    );
}

export function deleteValue(store: string, key: string): Promise<void> {
    return openDatabase().then(
        db =>
            new Promise<void>((resolve, reject) => {
                const tx = db.transaction(store, 'readwrite');
                tx.objectStore(store).delete(key);
                tx.oncomplete = () => resolve();
                tx.onerror = () =>
                    reject(
                        tx.error ?? new Error(`Failed to delete ${key} from ${store}`)
                    );
            })
    );
}

/** Adds a record to an auto-incrementing store and returns its new key. */
export function addRecord<T>(store: string, value: T): Promise<IDBValidKey> {
    return openDatabase().then(
        db =>
            new Promise<IDBValidKey>((resolve, reject) => {
                const tx = db.transaction(store, 'readwrite');
                const req = tx.objectStore(store).add(value);
                req.onsuccess = () => resolve(req.result);
                tx.onerror = () =>
                    reject(tx.error ?? new Error(`Failed to add record to ${store}`));
            })
    );
}

/** Reads every record in `store` whose `indexName` value equals `key`. */
export function getAllByIndex<T>(
    store: string,
    indexName: string,
    key: IDBValidKey
): Promise<T[]> {
    return openDatabase().then(
        db =>
            new Promise<T[]>((resolve, reject) => {
                const tx = db.transaction(store, 'readonly');
                const req = tx
                    .objectStore(store)
                    .index(indexName)
                    .getAll(key);
                req.onsuccess = () => resolve((req.result ?? []) as T[]);
                req.onerror = () =>
                    reject(
                        req.error ?? new Error(`Failed to read ${store} by ${indexName}`)
                    );
            })
    );
}

/** Counts records in `store` whose `indexName` value equals `key`. */
export function countByIndex(
    store: string,
    indexName: string,
    key: IDBValidKey
): Promise<number> {
    return openDatabase().then(
        db =>
            new Promise<number>((resolve, reject) => {
                const tx = db.transaction(store, 'readonly');
                const req = tx.objectStore(store).index(indexName).count(key);
                req.onsuccess = () => resolve(req.result);
                req.onerror = () =>
                    reject(
                        req.error ?? new Error(`Failed to count ${store} by ${indexName}`)
                    );
            })
    );
}

/** Deletes the records in `store` whose primary key is in `keys`. */
export function deleteRecords(store: string, keys: IDBValidKey[]): Promise<void> {
    return openDatabase().then(
        db =>
            new Promise<void>((resolve, reject) => {
                if (keys.length === 0) {
                    resolve();
                    return;
                }
                const tx = db.transaction(store, 'readwrite');
                const objectStore = tx.objectStore(store);
                for (const key of keys) {
                    objectStore.delete(key);
                }
                tx.oncomplete = () => resolve();
                tx.onerror = () =>
                    reject(
                        tx.error ?? new Error(`Failed to delete records from ${store}`)
                    );
            })
    );
}

/** Deletes every record in `store` whose `indexName` value equals `key`. */
export function deleteAllByIndex(
    store: string,
    indexName: string,
    key: IDBValidKey
): Promise<void> {
    return openDatabase().then(
        db =>
            new Promise<void>((resolve, reject) => {
                const tx = db.transaction(store, 'readwrite');
                const cursorReq = tx.objectStore(store).index(indexName).openCursor(key);
                cursorReq.onsuccess = () => {
                    const cursor = cursorReq.result;
                    if (cursor) {
                        cursor.delete();
                        cursor.continue();
                    }
                };
                tx.oncomplete = () => resolve();
                tx.onerror = () =>
                    reject(
                        tx.error ?? new Error(`Failed to clear ${store} by ${indexName}`)
                    );
            })
    );
}
